import React, { ReactElement, ReactNode } from 'react';
import { BrowserRouter as Router, Redirect } from 'react-router-dom';
import { CountryProvider } from './v2/providers/Country';
import { CrmisFeatureFlagsProvider } from '@crm-in-store/crmis-feature-flags';
import { AppErrorBoundary } from './components/AppError';
import { CountryCode } from './v3/types';
import { useUrl } from './hooks/useUrl';
import { TrackingProvider } from './providers/TrackingProvider';

interface AppProps {
  children: ReactNode;
}
const CommonProviders = ({ children }: AppProps): ReactElement => {
  return (
    <AppErrorBoundary>
      <Router>
        <Providers>{children}</Providers>
      </Router>
    </AppErrorBoundary>
  );
};

const URL_PARAM_COUNTRY_KEY = 'country';

/**
 * Component containing all providers except the implicit provider component
 * `Router`. This component should be nested inside the `Router` component
 * because it has a dependency on its state (via the `useLocation` hook).
 */
export const Providers = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { params } = useUrl();
  // @todo This actually isn't working. It will still show the UK site.
  const countryCode =
    params.get(URL_PARAM_COUNTRY_KEY) ?? CountryCode.Netherlands;

  return (
    <TrackingProvider>
      <CrmisFeatureFlagsProvider countryCode={countryCode}>
        <RedirectToUnitedKingdom />
        <CountryProvider>{children}</CountryProvider>
      </CrmisFeatureFlagsProvider>
    </TrackingProvider>
  );
};

/**
 * Redirects users who navigate with a country code from Great Britain to a URL
 * with a country code for the United Kingdom (i.e. `country=GB` to
 * `country=UK`). This is needed to ensure backward compatibility with existing
 * physical QR codes that are located in the store.
 */
export const RedirectToUnitedKingdom = (): JSX.Element | null => {
  const { pathname, params } = useUrl();
  const countryCode = params.get(URL_PARAM_COUNTRY_KEY);
  const isGB = countryCode === CountryCode.GreatBritain;
  return isGB ? (
    <Redirect
      to={{
        pathname: pathname,
        search: replaceCountryParameter(params, CountryCode.UnitedKingdom),
      }}
    />
  ) : null;
};

const replaceCountryParameter = (
  params: URLSearchParams,
  newCountryCode: string
) =>
  params
    .toString()
    .replace(/country=GB/i, `${URL_PARAM_COUNTRY_KEY}=${newCountryCode}`);

export default CommonProviders;
